import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { QueryKey } from '@tanstack/react-query'

import { getHttpStatus } from '#/shared/lib/api/error-handler'

/** True for the HTTP 400 the server returns when a cursor token is stale. There
 *  is nothing to retry — the cursor must be dropped — so pass this to a query's
 *  `retry` to fail fast and let recovery reload the first page. */
export function isInvalidCursor(error: unknown): boolean {
  return getHttpStatus(error) === 400
}

/** The slice of an infinite query this hook needs — kept loose so any
 *  `useInfiniteQuery` result satisfies it without an explicit cast. */
interface RecoverableQuery {
  error: unknown
  data: { pages: ReadonlyArray<unknown> } | undefined
}

/**
 * Cursor tokens are opaque and can go stale — the server then answers a
 * next-page fetch with HTTP 400 `Invalid cursor`. When that happens, drop the
 * loaded pages and reload from the first (cursorless) page. Guarded on already
 * having pages so a first-page 400 — which carries no cursor and means something
 * else — surfaces as a normal error instead of looping.
 */
export function useCursorRecovery(
  query: RecoverableQuery,
  queryKey: QueryKey,
): void {
  const queryClient = useQueryClient()
  useEffect(() => {
    if (!isInvalidCursor(query.error)) return
    if (!query.data?.pages.length) return
    void queryClient.resetQueries({ queryKey })
  }, [query.error, query.data, queryClient, queryKey])
}
